// frontend/src/api/articleService.ts
import apiClient from './index';
import type { 
    Article, 
    ArticleCreatePayload, 
    ArticleUpdatePayload,
    ArticleImageUploadResponse,
    CountResponse
} from '@/types';
import type { AxiosError } from 'axios';
import axios from 'axios';

interface FetchArticlesParams {
  skip?: number;
  limit?: number;
  status?: 'draft' | 'published' | 'archived' | 'all';
  tags?: string[];
  search?: string;
}

export const getArticles = async (params?: FetchArticlesParams): Promise<Article[]> => {
  try {
    // --- FIX: Add paramsSerializer to correctly format array parameters ---
    const response = await apiClient.get<Article[]>('/articles/', { 
        params,
        paramsSerializer: params => {
            const searchParams = new URLSearchParams();
            for (const key in params) {
                const value = params[key];
                if (Array.isArray(value)) {
                    value.forEach(v => searchParams.append(key, v));
                } else if (value !== null && value !== undefined) {
                    searchParams.append(key, value);
                }
            }
            return searchParams.toString();
        }
    });
    // --- End of FIX ---
    return response.data;
  } catch (error) {
     console.error("Error fetching articles:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching articles.");
  }
};

export const getArticlesCount = async (): Promise<CountResponse> => {
  try {
    const response = await apiClient.get<CountResponse>('/articles/count');
    return response.data;
  } catch (error) {
    console.error("Error fetching articles count:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching the article count.");
  }
};

export const getArticleByIdOrSlug = async (articleIdOrSlug: string): Promise<Article> => {
  try {
    const response = await apiClient.get<Article>(`/articles/${articleIdOrSlug}`);
    return response.data;
  } catch (error) {
     console.error(`Error fetching article by slug ${articleIdOrSlug}:`, error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error(`An unknown error occurred while fetching the article.`);
  }
};

export const createArticle = async (articleData: ArticleCreatePayload): Promise<Article> => {
    try {
        const response = await apiClient.post<Article>('/articles/', articleData);
        return response.data;
    } catch (error) {
        console.error("Error creating article:", error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: any }>;
             if (apiError.response?.data?.detail) {
                if(typeof apiError.response.data.detail === 'string') {
                    throw new Error(apiError.response.data.detail);
                } else if (Array.isArray(apiError.response.data.detail)) {
                    throw new Error(apiError.response.data.detail.map((e: any) => `${e.loc?.join(' -> ')}: ${e.msg}`).join('; '));
                }
            }
        }
        throw new Error("An unknown error occurred while creating the article.");
    }
};

export const updateArticle = async (articleId: number, articleData: ArticleUpdatePayload): Promise<Article> => {
   try {
    const response = await apiClient.put<Article>(`/articles/${articleId}`, articleData);
    return response.data;
  } catch (error) {
     console.error(`Error updating article ${articleId}:`, error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: any }>;
         if (apiError.response?.data?.detail) {
            if(typeof apiError.response.data.detail === 'string') {
                throw new Error(apiError.response.data.detail);
            } else if (Array.isArray(apiError.response.data.detail)) {
                throw new Error(apiError.response.data.detail.map((e: any) => `${e.loc?.join(' -> ')}: ${e.msg}`).join('; '));
            }
        }
    }
    throw new Error("An unknown error occurred while updating the article.");
  }
};

export const deleteArticle = async (articleId: number): Promise<void> => {
    try {
        await apiClient.delete(`/articles/${articleId}`);
    } catch (error) {
        console.error(`Error deleting article ${articleId}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw error;
    }
};

export const uploadArticleImage = async (imageFile: File): Promise<ArticleImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image_file', imageFile);
  try {
    const response = await apiClient.post<ArticleImageUploadResponse>('/articles/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading article image:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string, message?: string }>;
        if(apiError.response?.data?.detail) {
            throw new Error(apiError.response.data.detail);
        }
         if(apiError.response?.data?.message) {
            throw new Error(apiError.response.data.message);
        }
    }
    throw new Error("An unknown error occurred during image upload.");
  }
};

export const uploadImageForEditor = async (imageFile: File): Promise<{ image_url: string }> => {
  const formData = new FormData();
  formData.append('file', imageFile); 
  try {
    const response = await apiClient.post<{ image_url: string }>('/uploads/image/editor', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch(error) {
    console.error("Error uploading image for editor:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string, message?: string }>;
        if(apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        if(apiError.response?.data?.message) throw new Error(apiError.response.data.message);
    }
    throw new Error("An unknown error occurred during editor image upload.");
  }
};
